import { useCallback, useEffect, useMemo, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { fromUnixTime, isBefore } from "date-fns";

import { useFeatures } from "components/FeatureFlag";
import ModalTracker from "components/Modal/modalTracker";

import { usePlanOption, useSnoozeReminder } from ".";

const isExpired = (expiry) => {
  return expiry && isBefore(expiry, new Date());
};

const getInnerText = (target) => {
  if (target.innerText) return target.innerText;

  return target.closest("p, span, div")?.innerText;
};

const useInAppBannerManager = () => {
  const featureData = useFeatures("in-app-communication");
  const [banner, setBanner] = useState(null);
  const { isSnoozed, snooze } = useSnoozeReminder();
  const getPlanOption = usePlanOption();
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  const showBanner = useMemo(() => {
    return banner && !isExpired(banner?.content?.expiry);
  }, [banner]);

  const processCampaignData = useCallback(
    (campaignData, campaign) => {
      if (!campaignData || isSnoozed(campaign)) return;

      const { expiry, ...data } = campaignData;

      setBanner({
        content: { ...data, expiry: expiry ? fromUnixTime(expiry) : null },
        campaign: campaign,
      });
    },
    [isSnoozed]
  );

  const handleClickEvent = useCallback(
    async (event) => {
      if (showBanner) return;

      const triggerData =
        featureData?.trigger?.click?.[getInnerText(event.target)];
      if (!triggerData) return;

      const planOption = await getPlanOption();

      const campaign = triggerData?.[planOption];
      const campaignData = featureData?.campaigns?.[campaign];
      processCampaignData(campaignData, campaign);
    },
    [processCampaignData, getPlanOption, featureData, showBanner]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickEvent);
    const unsubscribe = ModalTracker.subscribe(setIsAnyModalOpen);

    return () => {
      document.removeEventListener("click", handleClickEvent);
      unsubscribe();
    };
  }, [handleClickEvent]);

  return {
    showBanner: Boolean(showBanner),
    banner: useMemo(() => {
      if (!showBanner) return undefined;

      return banner?.content
        ? { ...banner.content, forceOnTop: isAnyModalOpen }
        : undefined;
    }, [showBanner, banner, isAnyModalOpen]),
    onDismiss: useCallback(
      async ({ days } = {}) => {
        setBanner(null);
        datadogRum.addAction(
          `Dismiss banner ${banner?.content?.title}${
            days ? ` for ${days} days` : ""
          }`
        );
        if (!days || !banner.campaign) return;
        snooze(banner.campaign, days);
      },
      [banner, snooze]
    ),
  };
};

export default useInAppBannerManager;
